<template>
  <div class="bragg-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$nλ = 2d sinθ$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-wave-bragg"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 晶格原子排列 (Lattice) -->
          <g v-for="row in 3" :key="'r'+row">
             <line x1="40" :y1="130 + row*40" x2="360" :y2="130 + row*40" stroke="#1e293b" stroke-width="1" stroke-dasharray="4,4" />
             <circle v-for="col in 7" :key="'c'+col" :cx="40 + col*45" :cy="130 + row*40" r="4" fill="#64748b" />
          </g>

          <!-- 光程差三角形 (Geometry Link) -->
          <path v-if="isBragg" :d="pathDiffShape" fill="rgba(251, 191, 36, 0.15)" stroke="#fbbf24" stroke-width="1" stroke-dasharray="2,2" />

          <!-- 入射波 & 反射波 1 (Top layer) -->
          <path :d="ray1Path" fill="none" :stroke="isBragg ? '#facc15' : '#38bdf8'" :stroke-width="isBragg ? 3 : 1.5" :filter="isBragg ? 'url(#glow-wave-bragg)' : ''" />
          
          <!-- 入射波 & 反射波 2 (Bottom layer) -->
          <path :d="ray2Path" fill="none" :stroke="isBragg ? '#facc15' : '#38bdf8'" :stroke-width="isBragg ? 3 : 1.5" :opacity="isBragg ? 1 : 0.6" />

          <!-- 波峰/波前指示器 (体现波相位对齐) -->
          <g v-if="isBragg" class="bragg-success">
             <circle cx="350" cy="110" r="15" fill="none" stroke="#fbbf24" stroke-width="2">
                <animate attributeName="r" from="10" to="25" dur="1s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="1" to="0" dur="1s" repeatCount="indefinite" />
             </circle>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>掠射角 θ</label>
              <span class="val">{{ angle }}° <small>deg</small></span>
           </div>
           <div class="hud-item orange">
              <label>相干干涉状态</label>
              <span class="val" :style="{color: isBragg ? '#fbbf24' : '#64748b'}">{{ isBragg ? '布拉格衍射峰 (增强)' : '由于光程差 (消减中)' }}</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节 X 射线入射角 (θ)</label>
                 <el-slider v-model="angle" :min="10" :max="85" size="small"></el-slider>
              </div>
              <el-button type="warning" size="mini" plain @click="angle = 30">对齐布拉格峰 (30°)</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-aim"></i>
           <p><strong>核心揭示：</strong>光束在不同原子层反射。下层波比上层波多跑了一段路（光程差 $ \Delta = 2d \sin\theta $）。只有当这段路恰好是波长的整数倍时，反射波才不会发生自杀式的相位抵消。<strong>注意观察 30° 附近：</strong>此时波峰精准对齐，衍射强度达到极值。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BraggDiffractionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 20,
      d: 40, // Plane spacing
      lambda: 40, // Simulated wavelength
      t: 0
    }
  },
  computed: {
    isBragg() {
       // Bragg peak for n=1: 2 * 40 * sin(30) = 40. n=1 match.
       return Math.abs(this.angle - 30) < 2.5;
    },
    ray1Path() {
       const rad = this.angle * Math.PI / 180;
       const x0 = 100, y0 = 170; // Ray 1 reflection on p1
       return this.generateRayPath(x0, y0, rad, 0); // Offset 0
    },
    ray2Path() {
       const rad = this.angle * Math.PI / 180;
       const x0 = 100 + this.d / Math.tan(rad), y0 = 170 + this.d; 
       // Phase shift logic based on path difference 2d sin(theta)
       const pathDiff = 2 * this.d * Math.sin(rad);
       return this.generateRayPath(x0, y0, rad, pathDiff);
    },
    pathDiffShape() {
       const rad = this.angle * Math.PI / 180;
       const x0 = 200, y0 = 170;
       const x1 = x0 + this.d * Math.tan(rad);
       const x2 = x0 - this.d * Math.tan(rad);
       return `M ${x0} ${y0} L ${x0} ${y0+this.d} L ${x1} ${y0}`;
    }
  },
  methods: {
    generateRayPath(centerX, centerY, rad, extraPath) {
       let path = `M 40 ${centerY - (centerX-40)*Math.tan(rad)}`;
       // Simplified wave along ray - focus on phase alignment at reflection
       for (let i = 0; i < 350; i += 10) {
          const x = 40 + i;
          let y = centerY;
          // Sine wave representation along the path
          const phase = (i - centerX) * 0.2 - this.t + extraPath * 0.1;
          const amp = 5;
          const distToCenter = Math.abs(x - centerX);
          
          if (x < centerX) {
             y = centerY - distToCenter * Math.tan(rad) + Math.sin(phase) * amp;
          } else {
             y = centerY + distToCenter * Math.tan(rad) + Math.sin(phase) * amp;
          }
          path += ` L ${x} ${y}`;
       }
       return path;
    },
    loop() {
       this.t += 0.2;
       this.animId = requestAnimationFrame(this.loop);
    }
  },
  mounted() { this.loop(); },
  beforeDestroy() { cancelAnimationFrame(this.animId); }
}
</script>

<style scoped lang="scss">
.bragg-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #fefce8; padding: 10px 14px; border-radius: 10px; border: 1px solid #fef08a; display: flex; align-items: center; gap: 10px; i { color: #d97706; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #92400e; line-height: 1.5; strong { color: #78350f; } } }
</style>
